import { useEffect, useMemo, useState } from 'react';
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
} from '@mui/material';
import { AccountEntryService, InvoiceService, JobsService } from 'api';
import { Activity } from 'api/accountEntries/accountEntries.types';
import { JobQueryParams } from 'api/jobs/jobs.types';
import { isSuccessful } from 'api/utils';
import { StyledButton } from 'components/Buttons';
import { StyledAutocomplete, StyledTextField } from 'components/Inputs';
import { LoaderFullScreen } from 'components/Loaders/LoaderFullScreen';
import { isString } from 'lodash';
import { CurrencyCode, InvoiceClass } from 'pages/invoices/invoices.types';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { isInvoiceCorrective } from 'utils/invoiceFunctions.utils';
import { appToast } from 'utils/toast.utils';
import * as ST from '../../InvoiceAllocations.css';
import * as S from './AddAllocationDialog.css';
import { JobsSubtable } from './components/JobsSubtable';
import { FormData, JobsSubtableData } from './types';

type Props = {
  open: boolean;
  invoiceCode: string;
  invoiceClass: InvoiceClass;
  invoiceId: number;
  unallocatedAmount: number;
  currencyCode: CurrencyCode;
  accountEntriesLoading: boolean;
  onClose: () => void;
  onSubmit: () => void;
};

export const AddAllocationDialog = ({
  open,
  onClose,
  onSubmit,
  invoiceCode,
  invoiceClass,
  invoiceId,
  unallocatedAmount,
  currencyCode,
  accountEntriesLoading,
}: Props) => {
  const [selectedJob, setSelectedJob] = useState<JobsSubtableData | null>(null);
  const [activitiesOptions, setActivitiesOptions] = useState<Activity[]>([]);
  const [jobsQueryParams, setJobsQueryParams] = useState<JobQueryParams>({
    filterText: '',
    pageNumber: 1,
    resultSize: 10,
    jobStatus: ['IN_PROGRESS', 'FINALIZED'],
  });

  const { t } = useTranslation(['misc', 'invoice', 'currency']);

  const {
    control,
    watch,
    register,
    handleSubmit,
    formState: { isValid, errors },
    reset,
    setValue,
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      invoice: {
        __type: invoiceClass?.type.toLowerCase().includes('corrective')
          ? 'CorrectiveInvoice'
          : 'Invoice',
        id: invoiceId,
        invoiceClass,
      },
      invoiceAmount: 0,
      status: 'PENDING',
      creationDate: '',
      description: '',
      complaintReason: '',
      rejectionAcknowledged: false,
      wasEverComplained: false,
    },
  });

  const { getJobs, loading: jobsLoading, data } = JobsService.useGetJobs();
  const {
    data: activitiesData,
    loading: activitiesLoading,
    getInvoiceActivities,
  } = InvoiceService.useGetInvoiceActivities();
  const { loading, createAccountEntry } =
    AccountEntryService.useCreateAccountEntry();

  const isCorrective = useMemo(
    () => isInvoiceCorrective(invoiceClass),
    [invoiceClass],
  );

  const selectOnFocus = (
    e: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement, Element>,
  ) => e.target.select();

  const onFormSubmit = async (data: FormData) => {
    const res = await createAccountEntry(data);
    isSuccessful(res) &&
      appToast.success(t('message.createSuccess', { ns: 'misc' }));
    await onSubmit();
    onClose();
  };

  const handleExitDialog = () => {
    reset();
    setSelectedJob(null);
    setJobsQueryParams({
      filterText: '',
      pageNumber: 1,
      resultSize: 10,
      jobStatus: ['IN_PROGRESS', 'FINALIZED'],
    });
  };

  const isBackdropVisible: boolean = loading || accountEntriesLoading;
  const descriptionValue = watch('description');

  useEffect(() => {
    getJobs(jobsQueryParams);
  }, [getJobs, jobsQueryParams]);

  useEffect(
    () => setValue('description', jobsQueryParams.filterText),
    [jobsQueryParams.filterText, setValue],
  );

  useEffect(() => {
    if (selectedJob) setValue('job.id', selectedJob?.id);
  }, [selectedJob, setValue]);

  useEffect(() => {
    if (activitiesData?.length) {
      setActivitiesOptions((prev) => [...prev, ...activitiesData]);
    }
  }, [activitiesData]);

  useEffect(() => {
    getInvoiceActivities(invoiceId, isCorrective);
  }, [getInvoiceActivities, invoiceId, isCorrective]);

  return (
    <ST.StyledDialog
      open={open}
      onClose={onClose}
      maxWidth='lg'
      fullWidth
      TransitionProps={{ onExited: handleExitDialog }}
    >
      <LoaderFullScreen loading={isBackdropVisible} />
      <DialogTitle>
        {`${t('addAllocation', { ns: 'invoice' })} - ${t('invoice', {
          ns: 'invoice',
        })} ${invoiceCode}`}
      </DialogTitle>
      <DialogContent dividers>
        <S.FormContainer>
          <S.ListRow>
            <S.ListRowData>
              <StyledTextField
                id='invoice'
                size='small'
                padding='dense'
                margin='dense'
                label={t('invoice', { ns: 'invoice' })}
                defaultValue={invoiceCode}
                disabled
                width={280}
              />
            </S.ListRowData>
            <S.ErrorContainer />
          </S.ListRow>
          <S.ListRow>
            <S.ListRowData>
              <StyledTextField
                id='unallocatedAmount'
                size='small'
                padding='dense'
                margin='dense'
                label={t('unallocatedAmount', { ns: 'invoice' })}
                defaultValue={unallocatedAmount}
                disabled
                width={280}
              />
            </S.ListRowData>
            <S.ErrorContainer />
          </S.ListRow>

          <S.ListRow>
            <S.ListRowData>
              <StyledTextField
                {...register('invoiceAmount', {
                  required: true,
                  pattern: {
                    value: /^-?(?:\d+|\d{1,3}(?:,\d{3})+)(?:[\.|\,]\d+)?$/,
                    message: t('number', { ns: 'validation' }),
                  },
                  setValueAs: (v) => (isString(v) ? v?.replace(',', '.') : v),
                })}
                onFocus={selectOnFocus}
                id='invoiceAmount'
                size='small'
                padding='dense'
                margin='dense'
                label={t('allocationAmount', { ns: 'invoice' })}
                disabled={!selectedJob}
                width={280}
                error={!!errors.activity}
              />
            </S.ListRowData>
            <S.ErrorContainer>
              {errors.invoiceAmount && (
                <S.Error>
                  {errors?.invoiceAmount?.message ||
                    t('required', { ns: 'validation' })}
                </S.Error>
              )}
            </S.ErrorContainer>
          </S.ListRow>
          <S.ListRow>
            <S.ListRowData>
              <StyledTextField
                id='currency'
                size='small'
                padding='dense'
                margin='dense'
                label={t('currency', { ns: 'currency' })}
                defaultValue={currencyCode.currency}
                disabled
                width={280}
              />
            </S.ListRowData>
            <S.ErrorContainer />
          </S.ListRow>
          <S.ListRow>
            <S.ListRowData>
              <Controller
                name='activity'
                control={control}
                rules={{ required: true }}
                render={(props) => (
                  <StyledAutocomplete
                    {...props}
                    disablePortal
                    padding='dense'
                    getOptionLabel={(o) => (o as Activity).code}
                    options={activitiesOptions}
                    loading={activitiesLoading}
                    loadingText={t('combobox.searching', { ns: 'misc' })}
                    noOptionsText={t('combobox.noMatches', { ns: 'misc' })}
                    onChange={(_, data) => props.field.onChange(data)}
                    renderInput={(params) => (
                      <StyledTextField
                        {...params}
                        size='small'
                        error={!!errors.activity}
                        label={t('allocationActivity', { ns: 'invoice' })}
                        padding='dense'
                        margin='dense'
                        width={280}
                      />
                    )}
                  />
                )}
              />
            </S.ListRowData>
            <S.ErrorContainer>
              {errors.activity && (
                <S.Error>{t('required', { ns: 'validation' })}</S.Error>
              )}
            </S.ErrorContainer>
          </S.ListRow>
          <S.ListRow>
            <S.ListRowData>
              <StyledTextField
                {...register('description', {
                  maxLength: {
                    value: 256,
                    message: t('maxLength', {
                      ns: 'validation',
                      replace: { '0': '256' },
                    }),
                  },
                })}
                id='description'
                size='small'
                padding='dense'
                margin='dense'
                error={!!errors.description}
                label={t('description', { ns: 'misc' })}
                disabled={!selectedJob}
                InputLabelProps={{ shrink: !!descriptionValue }}
                width={280}
              />
            </S.ListRowData>
            <S.ErrorContainer>
              {errors.description && (
                <S.Error>{errors?.description?.message}</S.Error>
              )}
            </S.ErrorContainer>
          </S.ListRow>
        </S.FormContainer>
        <Divider />
        {data && (
          <JobsSubtable
            data={data.jobs}
            count={data.count}
            loading={jobsLoading}
            params={jobsQueryParams}
            setParams={setJobsQueryParams}
            selectedJob={selectedJob}
            onSelectJob={setSelectedJob}
          />
        )}
      </DialogContent>
      <DialogActions>
        <StyledButton
          $primary
          disabled={!isValid || !selectedJob}
          onClick={handleSubmit(onFormSubmit)}
        >
          {t('button.save', { ns: 'misc' })}
        </StyledButton>
        <StyledButton onClick={onClose}>
          {t('button.cancel', { ns: 'misc' })}
        </StyledButton>
      </DialogActions>
    </ST.StyledDialog>
  );
};
